/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { ComponentWrapper } from '@ihsm/ui-shared';
import { ReactComponent as Clear } from '../../assets/icons/clear.svg';
import './FormSearchInput.scss';

interface FormSearchInputProps {
  label?: string;
  className?: string;
  actionButton?: React.FC | React.ReactNode | React.ReactElement;
  searchInput?: string;
  setSearchInput?: (searchValue: string) => void;
  onSearch?: (search: string | undefined) => void;
  delay?: number;
  placeholder?: string;
  optionsList?: string[];
  disabled?: boolean;
}

export const FormSearchInput: React.FC<FormSearchInputProps> = ({
  label,
  className,
  actionButton,
  placeholder,
  searchInput = '',
  setSearchInput = () => {},
  optionsList = [],
  onSearch = () => {},
  delay = 300,
  disabled = false,
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setFocused] = useState(false);

  const triggerSearch = useCallback(
    (searchValue: string) => {
      if (searchValue && searchValue !== '') {
        onSearch(searchValue);
        return;
      }

      onSearch(undefined);
    },
    [onSearch]
  );

  const handleClear = () => {
    setSearchInput('');
    setFocused(false);
    onSearch(undefined);
  };

  const handleEnter: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      triggerSearch(searchInput);
    },
    [searchInput, triggerSearch]
  );

  const handleClickOptions = useCallback(
    (option: string) => {
      setSearchInput(option);
      triggerSearch(option);
      setFocused(false);
    },
    [setSearchInput, triggerSearch]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        setFocused(false);
      } else {
        setFocused(true);
      }

      setSearchInput(e.target.value);
    },
    [setSearchInput]
  );

  const filteredList = useMemo(() => {
    if (searchInput && searchInput !== '') {
      const updatedList = [...optionsList].filter((listItem) => {
        const filterClean = searchInput.toLowerCase().trim() || '';
        const titleClean = listItem.toLowerCase().trim();
        const isOptionValid = titleClean.indexOf(filterClean) !== -1;
        return isOptionValid;
      });

      return updatedList;
    }

    return optionsList;
  }, [searchInput, optionsList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      triggerSearch(searchInput);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, searchInput, onSearch, triggerSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchInput && searchInput === '') {
      setFocused(false);
    }

    if (inputRef.current?.value === '') {
      setFocused(false);
    }
  });

  return (
    <form
      onSubmit={handleEnter}
      className={`form-search-input ${className || ''}`}
    >
      <label
        className={`form-search-input__label ${
          className ? `${className}__label` : ''
        }`}
      >
        {label ? (
          <span
            className={`form-search-input__label-text ${
              className ? `${className}__label-text` : ''
            }`}
          >
            {label}
          </span>
        ) : null}
        <div
          className={`form-search-input__input-wrapper ${
            className ? `${className}__input-wrapper` : ''
          }`}
        >
          <input
            ref={inputRef}
            className={`form-search-input__input ${
              className ? `${className}__input` : ''
            }`}
            type="search"
            placeholder={placeholder}
            value={searchInput}
            onChange={handleInputChange}
            disabled={disabled}
          />
          {searchInput && (
            <button
              className="form-search-input__clear-icon"
              type="button"
              onClick={handleClear}
            >
              <Clear />
            </button>
          )}
          {isFocused && filteredList.length > 0 ? (
            <ul
              className={`form-search-input__options-list ${
                className ? `${className}__options-list` : ''
              }`}
            >
              {filteredList.map((option) => (
                <li
                  aria-selected
                  role="option"
                  key={option}
                  onKeyDown={() => handleClickOptions(option)}
                  onClick={() => handleClickOptions(option)}
                  className={`form-search-input__option-item ${
                    className ? `${className}__option-item` : ''
                  }`}
                >
                  {option}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </label>

      {actionButton ? <ComponentWrapper component={actionButton} /> : null}
    </form>
  );
};
